import Decimal from "decimal.js";
import { getApprovalTarget } from "hooks/allowances/useApprovalTarget";
import { Chain } from "viem/chains";
import { readContract } from "wagmi/actions";
import { DAI_LIKE_PERMIT1_TOKENS, PERMIT_TWO_CONTRACT_ADDRESS } from "../config/consts";
import { uint256toAmountType } from "../store/activity/getHistory";
import { IGlobalStore, SelectedToken } from "../store/global/GlobalStore";
import { Token } from "../store/global/TokenEnum";
import { PermitDomain, TokenList } from "../store/global/getAllTokenLists";
import { Permit2Abi } from "./abis/permit2Abi";
import { OrderType } from "./orderTypeUtils";
import { getTokenAddressBySymbol } from "./orderUtils";

export type Permit1Details = IGlobalStore["permits"]["permit1"];
export type Permit2Details = IGlobalStore["permits"]["permit2"];

export const PERMIT_ONE_TYPES_STANDARD = {
  Permit: [
    {
      name: "owner",
      type: "address",
    },
    {
      name: "spender",
      type: "address",
    },
    {
      name: "value",
      type: "uint256",
    },
    {
      name: "nonce",
      type: "uint256",
    },
    {
      name: "deadline",
      type: "uint256",
    },
  ],
} as const;
export const PERMIT_ONE_TYPES_DAI_LIKE = {
  EIP712Domain: [
    { name: "name", type: "string" },
    { name: "version", type: "string" },
    { name: "chainId", type: "uint256" },
    { name: "verifyingContract", type: "address" },
  ],
  Permit: [
    {
      name: "holder",
      type: "address",
    },
    {
      name: "spender",
      type: "address",
    },
    {
      name: "nonce",
      type: "uint256",
    },
    {
      name: "expiry",
      type: "uint256",
    },
    {
      name: "allowed",
      type: "bool",
    },
  ],
} as const;
export const PERMIT_TWO_TYPES = {
  EIP712Domain: [
    { name: "name", type: "string" },
    { name: "chainId", type: "uint256" },
    { name: "verifyingContract", type: "address" },
  ],
  PermitBatch: [
    { name: "details", type: "PermitDetails[]" },
    { name: "spender", type: "address" },
    { name: "sigDeadline", type: "uint256" },
  ],
  PermitDetails: [
    { name: "token", type: "address" },
    { name: "amount", type: "uint160" },
    { name: "expiration", type: "uint48" },
    { name: "nonce", type: "uint48" },
  ],
} as const;

export const getPermitParamTypes = (token: Token) => {
  if (DAI_LIKE_PERMIT1_TOKENS.includes(token)) return PERMIT_ONE_TYPES_DAI_LIKE;
  return PERMIT_ONE_TYPES_STANDARD;
};

interface CheckPermit2ApprovalParams {
  user: `0x${string}`;
  token: Token;
  tokenList: TokenList;
  chain: Chain;
}

// Permit Flow
/**
 * 1. Check if token has existing valid Permit 2 approval
 * 2. If yes - request a signature and trade.
 * 3. If no - check if token supports Permit 1
 * 4. If yes - request a signature and trade.
 * 5. If no - request a new Permit 2 approval.
 */

export const checkPermit2Approval = async ({
  user,
  token,
  tokenList,
  chain,
}: CheckPermit2ApprovalParams) => {
  if (!user) {
    console.log(`Skipping Permit check, since wallet is not connected`);
    return;
  }
  try {
    const tokenAddress = getTokenAddressBySymbol(tokenList, chain, token);
    const approvalTarget = getApprovalTarget();
    if (!approvalTarget) {
      console.error(`Could not get approval target`);
      return;
    }
    const data = await readContract({
      address: PERMIT_TWO_CONTRACT_ADDRESS,
      abi: Permit2Abi,
      functionName: "allowance",
      args: [user, tokenAddress, approvalTarget],
    });
    const [amount, expiration, nonce] = data;
    const balance = uint256toAmountType(amount, token, tokenList);
    return {
      amount: balance,
      expiration,
      nonce,
    };
  } catch (e) {
    console.error(`Could not check Permit 2 approval:`, e);
  }
};
export type PermitType = "permit1" | "permit2";
export const getPermitToUse = async (
  orderType: OrderType,
  userAddress: `0x${string}`,
  takerTokens: Array<SelectedToken>,
  tokenList: TokenList,
  chain: Chain
): Promise<PermitType> => {
  if (orderType === OrderType.ManyToOne) {
    return "permit2";
  } else {
    const token = takerTokens[0].token;
    const permit2Approval = await checkPermit2Approval({
      user: userAddress,
      token: token,
      tokenList,
      chain,
    });
    // First check if token has a valid Permit2 approval
    if (permit2Approval?.amount.value.gt(new Decimal(0))) {
      return "permit2";
    }
    // If not - check if the token supports permit1
    const tokenSupportsPermit1 = tokenList[token]?.permitDomain !== PermitDomain.NONE;
    /*
     *  TODO: FIX SO PERMIT1 WORKS IN FLOW
     * */

    if (tokenSupportsPermit1) {
      return "permit2";
    } else {
      return "permit2";
    }
  }
};

export const getExpiry = () => {
  const epoch = Math.floor(Date.now() / 1000);
  const epochPlus30Days = epoch + 60 * 60 * 24 * 30;
  return epochPlus30Days;
};

export const getPermitDomain = (permitDomain: PermitDomain) => {
  switch (permitDomain) {
    case PermitDomain.WITH_CHAINID:
      return {
        EIP712Domain: [
          { name: "name", type: "string" },
          { name: "chainId", type: "uint256" },
          { name: "verifyingContract", type: "address" },
        ],
      } as const;
    case PermitDomain.WITH_VERSION_AND_SALT:
      return {
        EIP712Domain: [
          { name: "name", type: "string" },
          { name: "version", type: "string" },
          { name: "verifyingContract", type: "address" },
          { name: "salt", type: "bytes32" },
        ],
      } as const;
    case PermitDomain.WITH_VERSION_AND_CHAINID:
      return {
        EIP712Domain: [
          { name: "name", type: "string" },
          { name: "version", type: "string" },
          { name: "chainId", type: "uint256" },
          { name: "verifyingContract", type: "address" },
        ],
      } as const;
    case PermitDomain.WITH_VERSION_AND_CHAINID_AND_SALT:
      return {
        EIP712Domain: [
          { name: "name", type: "string" },
          { name: "version", type: "string" },
          { name: "chainId", type: "uint256" },
          { name: "verifyingContract", type: "address" },
          { name: "salt", type: "bytes32" },
        ],
      } as const;
  }
};

export const getPermitFromStore = (store: IGlobalStore): Permit1Details | Permit2Details => {
  if (store.permitType === "permit1") {
    return store.permits.permit1;
  } else {
    return store.permits.permit2;
  }
};
